import React, { useState, useEffect } from "react";
import Button from '@material-ui/core/Button';
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Switch from "@material-ui/core/Switch";
import { useForm, Controller } from "react-hook-form";
import PasswordChecklist from "react-password-checklist";
import { useSetRecoilState, useRecoilState } from 'recoil';

import Spinner from '../../../../Common/UI/Spinner/Spinner';
import * as UserApi from '../../../../Common/Apis/UserApi';
import { useNotification } from "../../../../../core/contexts/NotificationContext";
import { userTabListState, userTabIndexState, userListRefreshState } from '../../../../../core/store/UserStore';
import { passwordGenegrator } from "../../../../../core/hooks/passwordGenerator";
import { useAuth } from "../../../../../core/contexts/AuthContext";

// User detail / edit form. Rendered with item=null in the "Add" tab (create) and with a row item
// in a per-user tab (update). (No permission gating in the Gate Reader portal — see User.js.)
const UserDetail = (props) => {
  // tabOffset = index of the first user-detail tab/panel: 1 (Users) + 1 ("Add").
  const { item, tabOffset = 2 } = props;
  const [userListRefresh, setUserListRefresh] = useRecoilState(userListRefreshState);
  const [userTabList, setUserTabList] = useRecoilState(userTabListState);
  const setTabIndex = useSetRecoilState(userTabIndexState);

  const [userRole, setUserRole] = useState("");
  const [userActive, setUserActive] = useState(item?.UserActive === "False" ? false : true);
  const [userPassword, setUserPassword] = useState("");
  const [userConfirmPassword, setUserConfirmPassword] = useState("");
  const [userRoleList, setUserRoleList] = useState([]);
  const [userRoleErr, setUserRoleErr] = useState(false);
  const [, setUserPasswordErr] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [apiErrorMsg, setapiErrorMsg] = useState("");

  const { showSuccess } = useNotification();
  const { chkRolePermissions } = useAuth();

  const [isLoading, setisLoading] = useState(false);

  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      txtUserCode: item?.UserCode || "",
      txtFirstName: item?.FirstName || "",
      txtLastName: item?.LastName || "",
      txtPhone: item?.Phone || "",
      txtMobile: item?.Mobile || "",
      txtEmail: item?.EmailAddress || "",
    }
  });

  const onSubmit = async (data) => {
    if (userRole === "") {
      setUserRoleErr(true);
      return;
    } else {
      setUserRoleErr(false);
    }
    setapiErrorMsg("");
    try {
      setisLoading(true);

      const updateCustomer = {
        "UserCode": data.txtUserCode,
        "IsActive": userActive,
        "UserFirstName": data.txtFirstName,
        "UserLastName": data.txtLastName,
        "UserPhone": data.txtPhone,
        "UserMobile": data.txtMobile,
        "UserEmailAddress": data.txtEmail,
        "RoleId": userRole,
        "UserPassword": userPassword
      };

      if (item) {
        await UserApi.UpdateCustomer(item.UserId, updateCustomer);

        const filtered = userTabList.filter((i) => i.UserId !== item.UserId);
        setUserTabList(filtered);
      } else {
        await UserApi.CreateCustomer(updateCustomer);
        resetForm();
      }

      showSuccess({ content: "Successfully saved", timeout: 4000 });

      setTabIndex(0);
      setUserListRefresh(!userListRefresh);
    } catch (err) {
      console.log("err", err);

      if (err.status === 400) {
        setapiErrorMsg(
          <div>
            Error occurred:
            <br />
            Check the following details
            {err.data?.Errors?.map((val, i) => (
              <div key={i}>- {val.Error}</div>
            ))}
          </div>
        );
      }
    }

    setisLoading(false);
  };

  const getAutoPassword = () => {
    const length = 12;
    const newPassword = passwordGenegrator(length);
    setUserPassword(newPassword);
    setUserConfirmPassword(newPassword);
  };

  const resetForm = () => {
    reset();
    setUserRole("");
    setUserPassword("");
    setUserConfirmPassword("");
  };

  useEffect(() => {
    // The "Add" panel renders this with item=null; nothing to activate there.
    if (!item) return;
    const thisIndex = userTabList.findIndex((i) => i.UserId === item.UserId);
    setTabIndex(thisIndex + tabOffset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const getUserRole = async () => {
      setisLoading(true);

      try {
        const resData = await UserApi.getUserRoles();
        setUserRoleList(resData);
        if (item) {
          setUserRole(item.RoleId);
        }
      } catch (err) {
        console.log("err", err);
      }

      setisLoading(false);
    };

    getUserRole();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <>
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="col-lg-7  mt-3">
        <div className="tab-content" id="myTabContent">
          <div className="card">
            <div className="card-body">
              <div className="row  mt-3">
                <div className="col-6 ">
                  {chkRolePermissions("Manage", "User Details", "Edit") &&
                    <Button
                      type="submit"
                      className="mb-3 ml-3"
                      style={{ fontWeight: "600" }}
                      variant="contained"
                      color="primary"
                    >
                      Save
                    </Button>
                  }
                </div>
                <div className="col-6 text-right ">
                  {!item &&
                    <Button
                      type="button"
                      className="mb-3 ml-3"
                      style={{ fontWeight: "600" }}
                      variant="contained"
                      onClick={resetForm}>
                      Reset
                    </Button>
                  }
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-sm-6">
                  <div className="p-number">
                    <Controller
                      name="txtUserCode"
                      rules={{ required: true }}
                      control={control}
                      render={({ field }) => <TextField
                        {...field}
                        style={{ width: "100%" }}
                        label=""
                        variant="outlined"
                        size="small"
                        error={!!errors.txtUserCode}
                        helperText={""}
                      />}
                    />
                  </div>
                  <span className="p-num-span mr-2">User Code</span>
                  {errors.txtUserCode && <p className="text-danger2">should not be blank</p>}
                </div>
                <div className="col-sm-6">
                  <div className="p-number">
                    <FormControl
                      style={{ width: "100%" }}
                      variant="outlined"
                      size="small"
                      error={userRoleErr}
                    >
                      <Select
                        onChange={e => setUserRole(e.target.value)}
                        value={userRole || ""}
                        inputProps={{ "aria-label": "Without label" }}
                      >
                        <MenuItem value=""></MenuItem>
                        {userRoleList?.map((i) => <MenuItem
                          key={i.RoleId} value={i.RoleId}>{i.RoleName}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </div>
                  <span className="p-num-span mr-2">Role</span>
                  {userRoleErr && <p className="text-danger2">should not be blank</p>}
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-sm-6">
                  <div className="p-number">
                    <Controller
                      name="txtFirstName"
                      rules={{ required: true }}
                      control={control}
                      render={({ field }) => <TextField
                        {...field}
                        style={{ width: "100%" }}
                        label=""
                        variant="outlined"
                        size="small"
                        error={!!errors.txtFirstName}
                        helperText={""}
                      />}
                    />
                  </div>
                  <span className="p-num-span mr-2">First Name</span>
                  {errors.txtFirstName && <p className="text-danger2">should not be blank</p>}
                </div>
                <div className="col-sm-6">
                  <div className="p-number">
                    <Controller
                      name="txtLastName"
                      rules={{ required: true }}
                      control={control}
                      render={({ field }) => <TextField
                        {...field}
                        style={{ width: "100%" }}
                        label=""
                        variant="outlined"
                        size="small"
                        error={!!errors.txtLastName}
                        helperText={""}
                      />}
                    />
                  </div>
                  <span className="p-num-span mr-2">Last Name</span>
                  {errors.txtLastName && <p className="text-danger2">should not be blank</p>}
                </div>
              </div>

              {!item &&
                <div className="row mt-3">
                  <div className="col-sm-6">
                    <div className="p-number">
                      <TextField
                        type={showPassword ? "text" : "password"}
                        style={{ width: "100%" }}
                        label=""
                        variant="outlined"
                        size="small"
                        onChange={e => setUserPassword(e.target.value)}
                        value={userPassword || ''}
                        helperText={""}
                      />
                    </div>
                    <span className="p-num-span mr-2">Password</span>
                  </div>
                  <div className="col-sm-6">
                    <div className="p-number">
                      <TextField
                        type={showPassword ? "text" : "password"}
                        style={{ width: "100%" }}
                        label=""
                        variant="outlined"
                        size="small"
                        onChange={e => setUserConfirmPassword(e.target.value)}
                        value={userConfirmPassword || ''}
                        helperText={""}
                      />
                    </div>
                    <span className="p-num-span mr-2">Confirm Password</span>
                  </div>
                  <div className="col-sm-6">
                    <div className="row">
                      <div className="col-6">
                        <div className="">
                          <Switch
                            checked={showPassword}
                            onChange={() => setShowPassword(!showPassword)}
                            color="primary"
                            name="chkEnabled"
                            inputProps={{ "aria-label": "primary checkbox" }}
                          />
                        </div>
                        <span className="p-num-span mr-2">Show Password</span>
                      </div>
                      <div className="col-6">
                        <Button color="primary" style={{ textTransform: 'none' }} onClick={getAutoPassword} variant="text">Generate Password</Button>
                      </div>
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <PasswordChecklist
                      style={{ background: "#f7f7f7" }}
                      rules={["minLength", "specialChar", "number", "match"]}
                      minLength={8}
                      value={userPassword}
                      valueAgain={userConfirmPassword}
                      onChange={(isValid) => setUserPasswordErr(!isValid)}
                    />
                  </div>
                </div>
              }

              <div className="row mt-3">
                <div className="col-sm-6">
                  <div className="p-number">
                    <Controller
                      name="txtMobile"
                      control={control}
                      render={({ field }) => <TextField
                        {...field}
                        style={{ width: "100%" }}
                        label=""
                        variant="outlined"
                        size="small"
                        helperText={""}
                      />}
                    />
                  </div>
                  <span className="p-num-span mr-2">Mobile</span>
                </div>
                <div className="col-sm-6">
                  <div className="p-number">
                    <Controller
                      name="txtPhone"
                      control={control}
                      render={({ field }) => <TextField
                        {...field}
                        style={{ width: "100%" }}
                        label=""
                        variant="outlined"
                        size="small"
                        helperText={""}
                      />}
                    />
                  </div>
                  <span className="p-num-span mr-2">Phone</span>
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-12">
                  <div className="p-number">
                    <Controller
                      name="txtEmail"
                      rules={{ required: true }}
                      control={control}
                      render={({ field }) => <TextField
                        {...field}
                        style={{ width: "100%" }}
                        label=""
                        variant="outlined"
                        size="small"
                        error={errors.txtEmail ? true : false}
                        helperText={""}
                      />}
                    />
                  </div>
                  <span className="p-num-span mr-2">Email</span>
                  {errors.txtEmail && <p className="text-danger2">should not be blank</p>}
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-12">
                  <div className="">
                    <Switch
                      checked={userActive}
                      onChange={() => setUserActive(!userActive)}
                      color="primary"
                      name="chkEnabled"
                      inputProps={{ "aria-label": "primary checkbox" }}
                    />
                  </div>
                  <span className="p-num-span mr-2">User Active</span>
                </div>
              </div>
              <div className="row mt-3 mb-2">
                <div className="col">
                  <div className="p-number text-danger2">{apiErrorMsg}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
    {isLoading && <Spinner />}
  </>;
};

export default UserDetail;
